"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import AboutStorySection from "@/components/sections/AboutStorySection";
import AboutEnablerSection from "@/components/sections/AboutEnablerSection";
import AboutStatsSection from "@/components/sections/AboutStatsSection";
import AboutParallaxSection from "@/components/sections/AboutParallaxSection";
import AboutTestimonialsCarousel from "@/components/sections/AboutTestimonialsCarousel";
import AboutFeaturesGrid from "@/components/sections/AboutFeaturesGrid";

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <div className="bg-background">
      {/* Hero Banner */}
      <section className="relative flex min-h-[260px] flex-col items-center justify-center overflow-hidden px-4 sm:min-h-[300px] md:min-h-[320px]">
        <Image
          src="/new/DSC08873.JPG"
          alt={t("images.aboutHero")}
          fill
          priority
          sizes="100vw"
          className="object-cover object-center"
          quality={80}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)",
          }}
          aria-hidden
        />
        <div className="relative z-10 text-center">
          <h1 className="text-2xl font-black tracking-tight text-white drop-shadow-lg sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl">
            {t("about.title")}
          </h1>
          <nav className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-white/90">
            <Link href="/" className="transition hover:text-white">
              {t("about.breadcrumbHome")}
            </Link>
            <span className="text-white/75" aria-hidden>
              –
            </span>
            <span className="text-white">{t("about.breadcrumbAbout")}</span>
          </nav>
        </div>
      </section>

      <AboutEnablerSection />
      <AboutParallaxSection />
      <AboutStorySection />
      <AboutStatsSection />

      {/* Elite Services / Intro Section */}
      <section className="overflow-hidden border-b border-border-soft bg-white py-12 sm:py-16 md:py-24 lg:py-28">
        <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 sm:gap-10 md:grid-cols-[1fr_1fr] md:items-center md:gap-12 md:px-6">
          <div className="min-w-0 space-y-4 sm:space-y-6">
            <span className="inline-block rounded-full bg-primary-soft px-3 py-1.5 text-xs font-semibold text-primary sm:px-4 sm:text-sm">
              {t("about.ultimateExperience")}
            </span>
            <h2 className="text-2xl font-bold uppercase tracking-tight text-slate-900 sm:text-3xl md:text-4xl">
              {t("about.aboutBigSport")}
            </h2>
            <p className="max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {t("about.aboutDescription")}
            </p>
            <Link
              href="/"
              className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-lg transition active:scale-[0.98] hover:bg-slate-800 hover:shadow-xl sm:px-6 sm:py-3.5"
            >
              {t("about.bookCourtNow")}
              <ArrowRight className="h-5 w-5 text-primary" />
            </Link>
          </div>
          <div className="relative h-[240px] min-w-0 overflow-hidden rounded-xl shadow-xl ring-1 ring-slate-200/50 sm:h-[280px] sm:rounded-2xl md:h-[360px] lg:h-[400px]">
            <Image
              src="/new/DSC06258.jpeg"
              alt={t("images.aboutTurfAction")}
              fill
              sizes="(max-width: 768px) 100vw, 600px"
              className="object-cover"
              loading="lazy"
              quality={75}
            />
          </div>
        </div>
      </section>

      <AboutFeaturesGrid />

      <AboutTestimonialsCarousel />
    </div>
  );
}
